import React, { useState, useEffect, useRef } from 'react';
import { productsApi } from '../utils/api';

/**
 * ProductAutocomplete Component
 *
 * Provides typeahead autocomplete for product search with:
 * - Real-time search as user types
 * - Dropdown with product thumbnails, prices, and badges
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Auto-fill product data on selection
 */
const ProductAutocomplete = ({
  value,
  onChange,
  onProductSelect,
  onBlurExtract,
  selectedProductTitle,
  placeholder = "Search products...",
  className = "",
  maxLength
}) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  const justSelectedRef = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync internal state with external value
  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
    }
  }, [value]);

  // Detect if query is a price search (e.g., "$150", "150", "$75")
  const detectPriceSearch = (query) => {
    const pricePattern = /^\$?(\d+(?:\.\d{2})?)$/;
    const match = query.trim().match(pricePattern);
    return match ? parseFloat(match[1]) : null;
  };

  // Search products with debounce
  const searchProducts = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    try {
      setIsLoading(true);

      // Check if this is a price search
      const priceValue = detectPriceSearch(searchQuery);

      let response;
      if (priceValue !== null) {
        // Price-based search: Get active products and filter by price
        // Exclude draft products from results
        response = await productsApi.getAll({ limit: 100, status: 'active' });

        if (response.success && response.data && response.data.products) {
          // Filter products by price (within $5 range), excluding drafts
          const priceRange = 5;
          const filteredData = response.data.products.filter(product => {
            // Exclude draft products
            if (product.status === 'draft') return false;
            const productPrice = parseFloat(product.price);
            return Math.abs(productPrice - priceValue) <= priceRange;
          }).sort((a, b) => {
            // Sort by price difference (closest first)
            const aDiff = Math.abs(parseFloat(a.price) - priceValue);
            const bDiff = Math.abs(parseFloat(b.price) - priceValue);
            return aDiff - bDiff;
          }).slice(0, 10);

          setResults(filteredData);
          setIsOpen(filteredData.length > 0);
        }
      } else {
        // Regular text search
        response = await productsApi.autocomplete(searchQuery, 10);

        if (response.success && response.data) {
          setResults(response.data);
          setIsOpen(response.data.length > 0);
        }
      }

      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error searching products:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
    setQuery(newValue);
    onChange?.(newValue);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      searchProducts(newValue);
    }, 300);
  };

  // Handle product selection
  const handleSelect = (product) => {
    justSelectedRef.current = true;
    setQuery(product.title);
    setIsOpen(false);
    setResults([]);
    onChange?.(product.title);
    onProductSelect?.(product);
  };

  // On blur: auto-select exact matches, or extract extra text from selected products
  const handleBlur = () => {
    // Skip if user just clicked a dropdown item (handleSelect already fired)
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    const trimmed = query.trim();
    if (!trimmed) return;

    // If a product was previously selected and user typed extra text after the name,
    // extract the extra text into special instructions
    if (selectedProductTitle && trimmed.toLowerCase().startsWith(selectedProductTitle.toLowerCase())) {
      const extra = trimmed.slice(selectedProductTitle.length).trim();
      if (extra) {
        // Reset name back to original product title, pass extra text up
        setQuery(selectedProductTitle);
        onChange?.(selectedProductTitle);
        onBlurExtract?.(extra);
        return;
      }
    }

    // Auto-select if typed text exactly matches a product in results
    if (results.length > 0) {
      const exactMatch = results.find(
        (p) => p.title.toLowerCase() === trimmed.toLowerCase()
      );
      if (exactMatch) {
        handleSelect(exactMatch);
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input Field */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          if (results.length > 0) {
            setIsOpen(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        maxLength={maxLength}
      />

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-floral-700"></div>
        </div>
      )}

      {/* Dropdown Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {results.map((product, index) => (
            <div
              key={product.id}
              onMouseDown={() => { justSelectedRef.current = true; }}
              onClick={() => handleSelect(product)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`
                flex items-center gap-3 p-3 cursor-pointer transition-colors
                ${selectedIndex === index
                  ? 'bg-floral-50 border-l-4 border-floral-700'
                  : 'hover:bg-gray-50 border-l-4 border-transparent'
                }
              `}
            >
              {/* Product Image */}
              <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md overflow-hidden">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center text-gray-400">
                          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      `;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-900 truncate">
                    {product.title}
                  </p>
                  {/* Shopify Badge */}
                  {product.is_shopify_synced && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-floral-100 text-floral-800">
                      Shopify
                    </span>
                  )}
                  {/* Low Stock Warning Badge */}
                  {product.track_inventory && product.inventory_quantity !== null && product.inventory_quantity < 5 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warn-bg text-warn-fg">
                      ⚠️ {product.inventory_quantity} left
                    </span>
                  )}
                </div>
                {product.product_type && (
                  <p className="text-sm text-gray-500 truncate">
                    {product.product_type}
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="flex-shrink-0 text-right">
                <p className="font-semibold text-gray-900">
                  ${parseFloat(product.price || 0).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {isOpen && !isLoading && results.length === 0 && query.length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
          No products found for "{query}"
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;
